/**
 * Team invitation utilities - migrated to use backend API
 */

import { api } from "@/lib/api";

export interface TeamInvitation {
  id: string;
  team_id: string;
  inviter_id: string;
  invitee_id: string;
  status: "pending" | "accepted" | "rejected";
  created_at: string;
  updated_at: string;
}

/**
 * Search for users by email or username
 */
export async function searchUsers(
  query: string
): Promise<Array<{ id: string; email: string; username?: string; name?: string; avatar?: string }>> {
  if (!query || query.trim().length < 2) {
    return [];
  }

  try {
    const data = await api<Array<{ id: string; email: string; username?: string; first_name?: string; last_name?: string; image_url?: string }>>(`/users/search?q=${encodeURIComponent(query)}`);
    
    return (data || []).map((user) => ({
      id: user.id,
      email: user.email,
      username: user.username,
      name: [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username || user.email?.split("@")[0],
      avatar: user.image_url || undefined,
    }));
  } catch (error) {
    console.error("Error in searchUsers:", error);
    return [];
  }
}

/**
 * Create a team invitation
 */
export async function createTeamInvitation(
  teamId: string,
  inviteeId: string
): Promise<TeamInvitation | null> {
  try {
    const data = await api<TeamInvitation>('/team-invitations', {
      method: 'POST',
      body: JSON.stringify({
        team_id: teamId,
        invitee_id: inviteeId,
      }),
    });
    return data;
  } catch (error) {
    console.error("Error creating invitation:", error);
    return null;
  }
}

/**
 * Accept a team invitation
 */
export async function acceptTeamInvitation(
  invitationId: string,
  userId: string
): Promise<boolean> {
  try {
    await api(`/team-invitations/${invitationId}/accept`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
    return true;
  } catch (error: any) {
    console.error("Error accepting invitation:", error);
    return false;
  }
}

/**
 * Reject a team invitation
 */
export async function rejectTeamInvitation(
  invitationId: string,
  userId: string
): Promise<boolean> {
  try {
    await api(`/team-invitations/${invitationId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
    return true;
  } catch (error) {
    console.error("Error rejecting invitation:", error);
    return false;
  }
}

/**
 * Get pending invitations for a user
 */
export async function getUserTeamInvitations(userId: string): Promise<TeamInvitation[]> {
  try {
    const data = await api<TeamInvitation[]>(`/team-invitations?userId=${userId}&status=pending`);
    return data || [];
  } catch (error) {
    console.error("Error fetching invitations:", error);
    return [];
  }
}
